import { type ReactNode, useEffect, useRef, useState } from "react";

/**
 * Inline PDF renderer (pdf.js → `<canvas>`) — works on desktop AND mobile.
 * An `<iframe>` PDF does NOT render inline on iOS Safari (it fires a silent
 * download); rendering each page to a canvas does, everywhere. pdf.js (~300 KB
 * + its worker) is DYNAMIC-imported so it only loads the first time a PDF is
 * actually shown — the portal bundle stays lean. On any failure it renders the
 * `fallback` (the caller's Apri/Scarica card), so a broken/huge PDF never dead-
 * ends the page.
 *
 * We import the **legacy** build (`pdfjs-dist/legacy/…`), NOT the modern one:
 * the modern build calls `Promise.withResolvers()` (Safari 17.4+ / iOS 17.4+
 * only) in BOTH the main lib AND the worker, so on any older iPhone the dynamic
 * import throws → the viewer fell back to the download card ("da mobile i PDF
 * non si vedono"). The legacy build is Babel-transpiled + core-js-polyfilled
 * (it DEFINES `Promise.withResolvers` if missing, in both scopes), so it runs on
 * older Safari. A main-thread polyfill alone can't fix this — the worker has its
 * own scope; the legacy build is the only cross-scope fix.
 */
export default function PortalPdfViewer({
  url,
  height,
  fallback,
}: {
  url: string;
  height: number;
  fallback: ReactNode;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let doc: any = null;
    const host = hostRef.current;
    setState("loading");
    void (async () => {
      try {
        // Legacy build (older-Safari safe — see the file header). NOT "pdfjs-dist".
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf.min.mjs");
        // The worker ships as a Vite-emitted asset; point pdf.js at its URL.
        const workerUrl = (await import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?url"))
          .default;
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        doc = await pdfjs.getDocument({ url }).promise;
        if (cancelled) {
          doc.destroy?.();
          return;
        }
        if (!host) return;
        host.replaceChildren();
        const cssWidth = host.clientWidth || 640;
        // Render at devicePixelRatio for crispness on retina, display at CSS size.
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const scale = Math.min((cssWidth - 8) / base.width, 3);
          const viewport = page.getViewport({ scale: scale * dpr });
          const canvas = document.createElement("canvas");
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
          canvas.style.maxWidth = "100%";
          canvas.className = "mx-auto mb-3 block rounded border border-border bg-white shadow-sm";
          const ctx = canvas.getContext("2d");
          if (!ctx) continue;
          host.appendChild(canvas);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await page.render({ canvasContext: ctx, viewport } as any).promise;
        }
        if (!cancelled) setState("ready");
      } catch (err) {
        // Surface the real cause (worker/compat/parse) so a mobile fallback is
        // never a mystery again — the empty catch hid exactly this.
        // eslint-disable-next-line no-console
        console.warn("[PortalPdfViewer] pdf.js failed to render:", err);
        if (!cancelled) setState("error");
      }
    })();
    return () => {
      cancelled = true;
      doc?.destroy?.();
    };
  }, [url]);

  if (state === "error") return <>{fallback}</>;
  return (
    <div
      className="relative overflow-auto rounded-[var(--portal-radius)] border border-border bg-muted/20 p-2"
      style={{ height }}
    >
      {state === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          Caricamento PDF…
        </div>
      )}
      <div ref={hostRef} />
    </div>
  );
}
